import { Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { Vector3 } from 'three';
import { AbstractDragHandler } from '../../cross-team-shared/annotation/shared/tool-helpers/abstract-drag-handler';
import { EventTrackingValue } from '../../team-apis/core/event-tracking';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import { viewTitles } from '../misc-services/side-view-rotation.service';
import { SideViewResizeControls } from '../render/controls/side-view-resize-controls';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { deleteIfDefined } from '../state/render-state';
import { getShapeForEventTracking } from '../utils/misc-utils';
import { subtractPoints } from '../utils/point-utils';

export abstract class AbstractSideViewDragHandler extends AbstractDragHandler {
  protected store: PointCloudContentStore;
  private lastWorldPoint: Vector3;
  private lastCanvasPoint: Point2D;
  protected abstract method: string;

  constructor(
    protected controls: SideViewResizeControls,
    initialEvent: MouseEvent,
    injector: Injector,
  ) {
    super(initialEvent, injector);
    this.lastWorldPoint = this.controls.getMouseWorldPoint(initialEvent);
    this.lastCanvasPoint = [initialEvent.clientX, initialEvent.clientY];

    this.store = injector.get(PointCloudContentStore);
    this.store('render')('currentEditSource').set({
      scene: 'side view',
      index: this.controls.axis,
    });
  }

  protected onMouseMove(event: MouseEvent) {
    this.onDrag(event);
    const worldPoint = this.controls.getMouseWorldPoint(event);
    this.lastWorldPoint = worldPoint;
    this.lastCanvasPoint = [event.clientX, event.clientY];
  }

  protected getWorldDelta(event: MouseEvent) {
    const worldPoint = this.controls.getMouseWorldPoint(event);
    return worldPoint.clone().sub(this.lastWorldPoint);
  }

  // Since our testing framework does not support sending movementX on mouse
  // events we need to compute it manually. Once this changes we can simplify
  // it to use browser native movementX
  protected getCanvasDelta(event: MouseEvent) {
    const eventPoint: Point2D = [event.clientX, event.clientY];
    const delta = subtractPoints(eventPoint, this.lastCanvasPoint);
    return delta;
  }

  protected override complete() {
    this.shellConnector.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      ...this.getEventContext(),
      method: this.method,
    });
  }

  protected override cleanUp() {
    this.store('render')('currentEditSource').setUsing(deleteIfDefined);
    super.cleanUp();
  }

  protected getEventContext(): EventTrackingValue {
    return {
      shapes: [getShapeForEventTracking(this.getFocusedShapeStore().state())],
      panel: viewTitles.eventTracking[this.controls.axis],
    };
  }

  protected getFocusedShapeStore() {
    const focusedCuboidStore = this.store.getFocusedShapeStore();
    assert(focusedCuboidStore, 'Drag in side view requires focused cuboid');
    return focusedCuboidStore;
  }

  protected abstract onDrag(event: MouseEvent): void;
}
